import React from 'react'

import { Section, SectionHeading, Button } from '../../../../components/ui'
import { bookingPath, defaultWashSlug, enterBooking } from '../../../../lib/booking-entry'
import { browserHistory } from '../../../../services/history'
import { readLowestWashPrice } from '../../../../lib/page-prices'
import { AREA_GROUPS, areaPath } from './areas'

import './areas-cta.css'

// SLOT 7.7 - AREAS AND THE FINAL CTA. Design direction §7.7.
//
// Two jobs, so two <Section>s and two H2s: the areas we cover, then the last
// thing the page asks for. Both are paper, so the band does not change and
// the alternating rhythm of §6 is untouched; they are separate sections
// because "an H2 per section" (§7) is a floor, and because only the second
// one is centred. `<Section centred>` exists for exactly this slot and
// nothing else on the page uses it: a centred 52-link index would be
// unreadable, a centred closing CTA is the one place the design allows it.
//
// THE AREAS LIST IS NOT DECORATION. It is the site's internal link graph:
// 46 location pages, every one of which is reached from the home page and
// from every other area page through this block. So every entry is a real
// <a href>, never an onClick, and the hrefs come from ./areas.js, which is a
// generated mirror of seo/areas.yml rather than 46 hand-typed names. Read
// the header of that file before changing anything about it: a slug is a URL
// Search Console already knows.
//
// GROUPED, NOT ALPHABETICAL. Fable's audit: an A to Z of 52 London place
// names is a wall, because nobody knows the alphabet of where they live.
// Grouped by compass point and then by county, a visitor finds their own
// area by looking in one group of ten instead of scanning fifty two. The
// grouping is derived from each area's outward codes, in ./areas.js.
//
// AND ON A PHONE THE GROUPS CLOSE. 52 links at the 44px tap minimum is about
// a thousand pixels of place names at the very bottom of a page the median
// visitor abandons at 36%, so each group is a <details>: nine 44px rows, and
// the one group you came for. Every anchor is still in the markup of a
// closed group, so a crawler reads all 46 either way; this is an accordion,
// never a fetch. At 960px and up every group is open, because a laptop has
// three columns of room and nothing to hide.
//
// THE DUPLICATE LIST, which is still open. app/views/gwmc/_site_areas.html.haml
// renders these same 46 links server-side, outside #app, as a navy strip
// under the React footer. That block is what a crawler reads without the
// bundle and it must keep working, but with this section built the page
// carries the list twice. It cannot be fixed from inside this directory:
// either that partial loses its `.site-areas-links` column and keeps the NAP
// and hours, or it is restyled to paint these exact classes and moved inside
// #app the way app/views/gwmc/_hero.html.haml is, so React's mount replaces
// it instead of adding to it. The classes below are flat and single-purpose
// so that second option is a mechanical translation.

// One area. The whole point of the block is that this is a plain anchor.
//
// The outward code rides along inside the link at 960px and up, where there
// is room for it: "car wash nw10" is a real query (seo/areas.yml), so the
// code belongs in the anchor text. Below 960px it is hidden by CSS rather
// than dropped from the markup, because "Shepherd's Bush W12" is half again
// as wide as "Shepherd's Bush" and the run fits two names to a line instead
// of three, and because the place name is the anchor text that carries the
// page anyway.
const AreaLink = ({ area, current }) => (
  <li className="areas-item">
    <a
      className={`gwmc-link areas-link${current ? ' areas-link--current' : ''}`}
      href={areaPath(area)}
      aria-current={current ? 'page' : undefined}>
      <span className="areas-link-name">{area.name}</span>
      <span className="areas-link-code">{area.outcodes[0]}</span>
    </a>
  </li>
)

// Whether the layout is the desktop one. The groups are <details>, and CSS
// cannot open a closed one, so the one thing about this section that the
// stylesheet genuinely cannot decide is decided here: open at 960px and up,
// closed below it. 960px is THE breakpoint (§4) and it is written once.
//
// There is no resize listener on first paint: the initial state is read
// synchronously so the first render is already right at both widths, and the
// listener after it is for a rotated phone and a dragged window.
const DESKTOP = '(min-width: 960px)'

const useDesktop = () => {
  const [desktop, setDesktop] = React.useState(
    () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(DESKTOP).matches
  )

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined

    const query = window.matchMedia(DESKTOP)
    const onChange = event => setDesktop(event.matches)
    setDesktop(query.matches)

    // addListener is the Safari 13 spelling. It is deprecated, not absent,
    // and this is the whole of the fallback.
    if (query.addEventListener) {
      query.addEventListener('change', onChange)
      return () => query.removeEventListener('change', onChange)
    }

    query.addListener(onChange)
    return () => query.removeListener(onChange)
  }, [])

  return desktop
}

const AreasIndex = () => {
  const desktop = useDesktop()
  // On an area page the visitor's own area is marked rather than linked away
  // from, the same as the server block's `is-current`.
  const here = browserHistory.location.pathname

  return (
    <nav className="areas-groups" aria-label="Areas we cover">
      {AREA_GROUPS.map(group => (
        <details className="areas-group" key={group.id} open={desktop}>
          <summary className="areas-group-summary">
            {/* <summary> may hold exactly one heading element, so the H3 the
                section needs is also the row that opens the group. */}
            <h3 className="gwmc-h3 areas-group-name">{group.name}</h3>
            <span className="areas-group-count">{group.areas.length}</span>
          </summary>
          <ul className="areas-list">
            {group.areas.map(area => (
              <AreaLink key={area.slug} area={area} current={areaPath(area) === here} />
            ))}
          </ul>
        </details>
      ))}
    </nav>
  )
}

// The closing CTA. Its figure comes from the server, like every other price on
// the page, and no price is ever baked into the bundle (§3: a baked price goes
// stale and nobody notices).
//
// IT WAS WRONG UNTIL 5 OCTOBER 2026, and nothing typed it. This file read
// `gwmc:prices` and took the minimum over its values; that tag carries
// `large_car` and `congestion_zone` beside the six washes, so the minimum was
// the Congestion Zone surcharge and the button on the home page and all 52
// area pages read "Book a wash from £18". The cheapest wash is £69. Derived
// from the wrong source is as wrong as typed and much harder to see, so the
// figure comes from lib/page-prices.js, which takes the washes off
// `gwmc:catalogue` and nothing else.
//
// Null when the server published nothing we can price, and then the button asks
// for the booking without quoting a figure: a wrong price on the closing CTA is
// a refund conversation.
const FinalCta = () => {
  const from = readLowestWashPrice()

  return (
    <Section centred className="final-cta">
      <SectionHeading intro="A professional valeter comes to you with their own water and power. Same-day washes available.">
        Your car, spotless, without leaving home
      </SectionHeading>

      <Button
        className="final-cta-btn"
        variant="primary"
        href={bookingPath(defaultWashSlug())}
        onClick={event => {
          event.preventDefault()
          enterBooking(defaultWashSlug(), 'final_cta')
        }}>
        {from ? `Book now from ${from}` : 'Book now'}
      </Button>

      <p className="gwmc-caption final-cta-note">
        We keep your card on file to make repeat bookings easy.
      </p>
    </Section>
  )
}

export const AreasAndCta = () => (
  <>
    <Section id="areas" className="areas">
      <SectionHeading
        intro="Every London borough, plus nearby Hertfordshire, Essex, Kent and Surrey. Find your area.">
        Everywhere in London
      </SectionHeading>
      <AreasIndex />
    </Section>

    <FinalCta />
  </>
)
